import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHint } from '../common/PageHint';
import { AdaptiveTabs, AdaptiveTabItem } from '../common/AdaptiveTabs';
import { getExpertQuestionnaires, downloadClientQuestionnairePdf } from '../../services/questionnaires/questionnaire.service';
import { Questionnaire } from '../../types/questionnaire';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  LinearProgress,
  CardActionArea,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import { BaseDialog } from '../common/BaseDialog';
import { questionnairesConstructionService } from '../../services/questionnaires-construction/questionnaires-construction.service';
import { ConstructionTemplate } from '../../types/questionnaire-construction';
import { toastService } from '../../services/toast.service';

interface ClientQuestionnairesTabProps {
  clientId: string;
}

type QuestionnaireStatus = 'completed' | 'not-completed';

const STATUS_TABS: AdaptiveTabItem[] = [
  { value: 'completed', label: 'Завершені' },
  { value: 'not-completed', label: 'Незавершені' },
];

// completionRate — лише довідковий прогрес (% заповнених полів, включно з необов'язковими),
// статус "завершено" рахується по обов'язкових питаннях (бекенд) — тому тут лише 2 реальні стани,
// "частково пройдені" по completionRate було б "мертвим" табом без чіткого критерію.
const getStatus = (q: Questionnaire): QuestionnaireStatus =>
  q.status === 'completed' ? 'completed' : 'not-completed';

const EMPTY_MESSAGES: Record<QuestionnaireStatus, string> = {
  completed: 'Клієнт ще не завершив жодного опитувальника.',
  'not-completed': 'Усі опитувальники клієнта завершені.',
};

export const ClientQuestionnairesTab: React.FC<ClientQuestionnairesTabProps> = ({ clientId }) => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentTab = (STATUS_TABS.some(t => t.value === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'completed') as QuestionnaireStatus;

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [ownTemplates, setOwnTemplates] = useState<ConstructionTemplate[]>([]);
  const [assignTemplateId, setAssignTemplateId] = useState<number | ''>('');
  const [assigning, setAssigning] = useState(false);

  const fetchQuestionnaires = async () => {
    if (!clientId) return;
    setLoading(true);
    const data = await getExpertQuestionnaires(clientId);
    setQuestionnaires(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchQuestionnaires();
  }, [clientId]);

  const handleOpenAssignDialog = async () => {
    setAssignTemplateId('');
    setAssignDialogOpen(true);
    const templates = await questionnairesConstructionService.getTemplates();
    const alreadyAssignedIds = new Set(questionnaires.map(q => q.id));
    setOwnTemplates(templates.filter(t => !t.isSystem && t.status === 'published' && !alreadyAssignedIds.has(t.id)));
  };

  const handleAssign = async () => {
    if (!assignTemplateId) return;
    setAssigning(true);
    try {
      await questionnairesConstructionService.assignTemplate(assignTemplateId, [Number(clientId)]);
      toastService.success('Опитування призначено клієнту');
      setAssignDialogOpen(false);
      await fetchQuestionnaires();
    } catch {
      toastService.error('Не вдалося призначити опитування');
    } finally {
      setAssigning(false);
    }
  };

  const handleCardClick = (templateId: number) => {
    navigate(`/client/${clientId}/questionnaires/${templateId}`);
  };

  const handleDownloadPdf = async (event: React.MouseEvent, q: Questionnaire) => {
    event.stopPropagation();
    setDownloadingId(q.id);
    try {
      await downloadClientQuestionnairePdf(clientId, q.id, q.name);
    } catch (err) {
      console.error('Failed to download questionnaire PDF', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const visibleQuestionnaires = questionnaires.filter(q => getStatus(q) === currentTab);

  return (
    <Box data-i-doc={PageIndices.EXPERT_CLIENT_QUESTIONNAIRES}>
      <PageHint iDoc={PageIndices.EXPERT_CLIENT_QUESTIONNAIRES} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" gutterBottom>Опитувальники клієнта</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={handleOpenAssignDialog}>
          Призначити опитування
        </Button>
      </Box>
      <AdaptiveTabs tabs={STATUS_TABS} searchParamKey="tab" defaultValue="completed" />

      <BaseDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        title="Призначити опитування"
        actions={
          <Button variant="contained" onClick={handleAssign} disabled={!assignTemplateId || assigning}>
            {assigning ? <CircularProgress size={20} color="inherit" /> : 'Призначити'}
          </Button>
        }
      >
        {ownTemplates.length === 0 ? (
          <Typography color="text.secondary">
            Немає власних опитувань, які ще не призначені цьому клієнту.
          </Typography>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel>Опитування</InputLabel>
            <Select
              value={assignTemplateId}
              label="Опитування"
              onChange={(e) => setAssignTemplateId(e.target.value as number)}
            >
              {ownTemplates.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.nestNameI18n?.uk ?? `Опитувальник #${t.id}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </BaseDialog>
      <Box sx={{ pt: 2 }}>
      {visibleQuestionnaires.length === 0 ? (
        <Typography>{EMPTY_MESSAGES[currentTab]}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
          {visibleQuestionnaires.map((q) => (
            <Card key={q.id} sx={{ flex: '1 1 280px', minWidth: 280, maxWidth: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardActionArea
                onClick={() => handleCardClick(q.id)}
                sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="div">
                    {q.name}
                  </Typography>
                  {q.invitedByExpert && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Запросив: {q.invitedByExpert.name ?? `Експерт #${q.invitedByExpert.id}`}
                    </Typography>
                  )}
                  <Typography sx={{ mt: 1.5 }} color="text.secondary">
                    {q.description}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  {q.completionRate !== null && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Прогрес: {q.completionRate}%
                      </Typography>
                      <LinearProgress variant="determinate" value={q.completionRate} />
                    </Box>
                  )}
                  {q.lastSavedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Останнє збереження: {formatDate(q.lastSavedAt)}
                    </Typography>
                  )}
                </Box>
              </CardActionArea>
              {(q.completionRate ?? 0) > 0 && (
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <Tooltip title="Скачати PDF з результатами">
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDownloadPdf(e, q)}
                        disabled={downloadingId === q.id}
                      >
                        {downloadingId === q.id ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </CardActions>
              )}
            </Card>
          ))}
        </Box>
      )}
      </Box>
    </Box>
  );
};
